import React, { useState } from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  OnChangeFn,
} from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { VehicleListItem } from '@/app/types/vehicle';
import { useDebounce } from '@/hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// Define a type for the custom meta properties
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
  enableColumnFilter?: boolean;
};

interface VehicleDataTableProps {
  data: VehicleListItem[];
  columns: ExtendedColumnDef<VehicleListItem>[];
  isLoading: boolean;
  pageCount?: number;
  page: number;
  setPage: (page: number) => void;
  onSearchChange: (value: string) => void;
  searchValue: string;
  onSortingChange?: (sorting: SortingState) => void;
  sortingState?: SortingState;
  onFilterChange?: (
    filters: Record<string, string | number | boolean | { from?: string; to?: string }>
  ) => void;
}

export function VehicleDataTable({
  data,
  columns,
  isLoading,
  pageCount = 0,
  page,
  setPage,
  onSearchChange,
  searchValue,
  onSortingChange,
  sortingState = [],
  onFilterChange,
}: VehicleDataTableProps): React.ReactElement {
  const [sorting, setSorting] = useState<SortingState>(sortingState);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const navigate = useNavigate();

  // Use debounce hook for search
  const [searchInput, setSearchInput] = useState(searchValue);
  const debouncedSearch = useDebounce(searchInput, 500);

  // Call parent's onSearchChange when debounced value changes
  React.useEffect(() => {
    onSearchChange(debouncedSearch);
  }, [debouncedSearch, onSearchChange]);

  // Use useEffect to send filter changes to parent component
  React.useEffect(() => {
    if (onFilterChange) {
      const apiFilters: Record<
        string,
        string | number | boolean | { from?: string; to?: string }
      > = {};

      columnFilters.forEach((filter) => {
        const value = filter.value;
        if (value !== undefined && value !== null && value !== '') {
          if (typeof value === 'object' && 'from' in value) {
            const dateRange = value as { from?: string; to?: string };
            if (dateRange.from || dateRange.to) {
              apiFilters[filter.id] = dateRange;
            }
          } else if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            typeof value === 'boolean'
          ) {
            apiFilters[filter.id] = value;
          }
        }
      });

      const timeoutId = setTimeout(() => {
        onFilterChange(apiFilters);
      }, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [columnFilters, onFilterChange]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = updater instanceof Function ? updater(sorting) : updater;
    setSorting(newSorting);
    if (onSortingChange) {
      onSortingChange(newSorting);
    }
    setPage(0);
  };

  const handleColumnFiltersChange = (
    updater: ColumnFiltersState | ((state: ColumnFiltersState) => ColumnFiltersState)
  ): void => {
    const newFilters = updater instanceof Function ? updater(columnFilters) : updater;
    setColumnFilters(newFilters);
    setPage(0);
  };

  const table = useReactTable({
    data,
    columns: columns as ColumnDef<VehicleListItem, unknown>[],
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: pageCount,
  });

  const handleRowClick = (e: React.MouseEvent, id: number): void => {
    const path = `/workshop/vehicles/detail/${id}`;

    if ((e.ctrlKey || e.metaKey) && window.api?.tabs) {
      e.preventDefault();
      window.api.tabs.create(path, `Vehicle #${id}`);
    } else {
      navigate(path);
    }
  };

  const renderPaginationControls = (): JSX.Element => (
    <div className="flex items-center justify-between w-full">
      <div className="text-sm text-muted-foreground">
        Page {page + 1} of {pageCount || 1}
      </div>
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page - 1)}
          disabled={page === 0 || isLoading}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page + 1)}
          disabled={page + 1 >= pageCount || isLoading}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  const getColumnKey = (column: ExtendedColumnDef<VehicleListItem, unknown>): string => {
    return column.id ?? (column as { accessorKey?: string }).accessorKey ?? '';
  };

  const renderFilterControl = (
    column: ExtendedColumnDef<VehicleListItem, unknown>
  ): JSX.Element | null => {
    const columnKey = getColumnKey(column);
    const tableColumn = table.getColumn(columnKey);
    const columnMeta = column.meta;

    if (!tableColumn || !column.enableColumnFilter || !columnMeta?.variant) {
      return null;
    }

    const filterValue = tableColumn.getFilterValue();

    if (columnMeta.variant === 'text' || columnMeta.variant === 'number') {
      return (
        <Input
          type={columnMeta.variant === 'number' ? 'number' : 'text'}
          value={(filterValue ?? '') as string}
          onChange={(e) => tableColumn.setFilterValue(e.target.value)}
          placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`}
          className="h-8 w-full max-w-xs"
        />
      );
    }

    if (columnMeta.variant === 'select' && columnMeta.options) {
      const ALL_VALUE = '__ALL__';
      return (
        <Select
          value={(filterValue ?? ALL_VALUE) as string}
          onValueChange={(value) =>
            tableColumn.setFilterValue(value === ALL_VALUE ? undefined : value)
          }
        >
          <SelectTrigger className="h-8 w-full max-w-xs">
            <SelectValue
              placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>All</SelectItem>
            {columnMeta.options.map((option) => (
              <SelectItem key={String(option.value)} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Global Search & Column Visibility Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search registration, make, model..."
            value={searchInput || ''}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8 w-full"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9">
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {columns.map((column) => {
              const columnKey = getColumnKey(column);
              const tableCol = table.getColumn(columnKey);
              return (
                tableCol?.getCanHide() && (
                  <DropdownMenuCheckboxItem
                    key={columnKey}
                    className="capitalize"
                    checked={tableCol.getIsVisible()}
                    onCheckedChange={(value) => tableCol.toggleVisibility(!!value)}
                  >
                    {column.meta?.label || columnKey}
                  </DropdownMenuCheckboxItem>
                )
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Row for Individual Column Filters */}
      <div className="flex items-center gap-2 flex-wrap p-2 border rounded-md bg-card">
        {columns.map(
          (column) =>
            column.enableColumnFilter && (
              <div key={getColumnKey(column)} className="flex flex-col space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  {column.meta?.label || getColumnKey(column)}
                </label>
                {renderFilterControl(column)}
              </div>
            )
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex items-center justify-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={(e) => handleRowClick(e, row.original.id)}
                  className="cursor-pointer hover:bg-muted"
                  title="Click to view, Ctrl/Cmd+Click to open in new tab"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No vehicles found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      {renderPaginationControls()}
    </div>
  );
}
